import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Search,
  Star,
  Download,
  Filter,
  Eye,
  ArrowRight,
  Sparkles,
} from 'lucide-react';
// TODO: Replace mock data with Supabase integration
// Example: const { data, error } = await supabase.from('templates').select('*')

const CATEGORIES = [
  { slug: 'business-finance', label: 'Business & Finance', emoji: '💼' },
  { slug: 'project-management', label: 'Project Management', emoji: '📋' },
  { slug: 'marketing-sales', label: 'Marketing & Sales', emoji: '📈' },
  { slug: 'personal-productivity', label: 'Personal Productivity', emoji: '⚡' },
  { slug: 'education-learning', label: 'Education & Learning', emoji: '📚' },
  { slug: 'health-fitness', label: 'Health & Fitness', emoji: '💪' },
  { slug: 'real-estate', label: 'Real Estate', emoji: '🏠' },
  { slug: 'hr-people', label: 'HR & People', emoji: '👥' },
  { slug: 'inventory-operations', label: 'Inventory & Operations', emoji: '📦' },
  { slug: 'creative-content', label: 'Creative & Content', emoji: '🎨' },
];

const PLATFORMS = ['All', 'Google Sheets', 'Excel', 'Notion', 'Airtable'];

const PRICING_OPTIONS = ['All', 'Free', 'Premium'];

const PLATFORM_COLORS = {
  'Google Sheets': 'bg-green-500',
  Excel: 'bg-emerald-600',
  Notion: 'bg-gray-600',
  Airtable: 'bg-yellow-500',
};

const MOCK_TEMPLATES = [
  {
    id: 1,
    title: 'Startup Financial Model',
    description:
      'Complete financial projection model with revenue forecasting, expense tracking, and investor-ready dashboards for early-stage startups.',
    platform: 'Google Sheets',
    category: 'business-finance',
    price: null,
    rating: 4.8,
    downloads: 12450,
    creator: 'FinanceGuru',
    featured: true,
    thumbnail:
      'https://placehold.co/400x250/EEF2FF/3B82F6?text=Template+Preview',
  },
  {
    id: 2,
    title: 'Agile Sprint Tracker',
    description:
      'Manage your agile sprints with backlog prioritization, burndown charts, and team velocity tracking all in one place.',
    platform: 'Notion',
    category: 'project-management',
    price: 9.99,
    rating: 4.9,
    downloads: 8320,
    creator: 'AgileWorks',
    featured: true,
    thumbnail:
      'https://placehold.co/400x250/EEF2FF/3B82F6?text=Template+Preview',
  },
  {
    id: 3,
    title: 'Social Media Content Calendar',
    description:
      'Plan and schedule your social media posts across all platforms with analytics tracking and content approval workflows.',
    platform: 'Airtable',
    category: 'marketing-sales',
    price: 4.99,
    rating: 4.6,
    downloads: 6780,
    creator: 'ContentPro',
    featured: true,
    thumbnail:
      'https://placehold.co/400x250/EEF2FF/3B82F6?text=Template+Preview',
  },
  {
    id: 4,
    title: 'Personal Budget Planner',
    description:
      'Track income, expenses, and savings goals with automated charts and monthly summaries to keep your finances on track.',
    platform: 'Google Sheets',
    category: 'personal-productivity',
    price: null,
    rating: 4.7,
    downloads: 21500,
    creator: 'BudgetBoss',
    featured: false,
    thumbnail:
      'https://placehold.co/400x250/EEF2FF/3B82F6?text=Template+Preview',
  },
  {
    id: 5,
    title: 'Student Grade Tracker',
    description:
      'Monitor grades, assignments, and GPA across all courses with weighted calculations and progress visualization.',
    platform: 'Excel',
    category: 'education-learning',
    price: null,
    rating: 4.5,
    downloads: 15200,
    creator: 'EduTools',
    featured: false,
    thumbnail:
      'https://placehold.co/400x250/EEF2FF/3B82F6?text=Template+Preview',
  },
  {
    id: 6,
    title: 'Workout & Meal Planner',
    description:
      'Comprehensive fitness tracker with workout routines, meal planning, calorie tracking, and progress photos log.',
    platform: 'Notion',
    category: 'health-fitness',
    price: 7.99,
    rating: 4.4,
    downloads: 4300,
    creator: 'FitTrack',
    featured: false,
    thumbnail:
      'https://placehold.co/400x250/EEF2FF/3B82F6?text=Template+Preview',
  },
  {
    id: 7,
    title: 'Property Investment Analyzer',
    description:
      'Evaluate rental properties with ROI calculations, mortgage amortization, and cash flow projections for real estate investors.',
    platform: 'Excel',
    category: 'real-estate',
    price: 14.99,
    rating: 4.9,
    downloads: 3100,
    creator: 'PropCalc',
    featured: false,
    thumbnail:
      'https://placehold.co/400x250/EEF2FF/3B82F6?text=Template+Preview',
  },
  {
    id: 8,
    title: 'Employee Onboarding Tracker',
    description:
      'Streamline new hire onboarding with task checklists, document tracking, and automated reminders for HR teams.',
    platform: 'Airtable',
    category: 'hr-people',
    price: null,
    rating: 4.3,
    downloads: 5600,
    creator: 'HRFlow',
    featured: false,
    thumbnail:
      'https://placehold.co/400x250/EEF2FF/3B82F6?text=Template+Preview',
  },
  {
    id: 9,
    title: 'Warehouse Inventory System',
    description:
      'Full inventory management with stock levels, reorder alerts, barcode tracking, and supplier management for warehouses.',
    platform: 'Google Sheets',
    category: 'inventory-operations',
    price: 12.99,
    rating: 4.6,
    downloads: 7400,
    creator: 'StockMaster',
    featured: false,
    thumbnail:
      'https://placehold.co/400x250/EEF2FF/3B82F6?text=Template+Preview',
  },
];

function getCategoryLabel(slug) {
  const cat = CATEGORIES.find((c) => c.slug === slug);
  return cat ? cat.label : slug;
}

function TemplateCard({ template, large = false }) {
  const platformColor = PLATFORM_COLORS[template.platform] || 'bg-gray-500';

  return (
    <div
      className={`group relative rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden ${
        large ? 'md:col-span-1' : ''
      }`}
    >
      {/* Thumbnail */}
      <div className="relative overflow-hidden">
        <img
          src={template.thumbnail}
          alt={template.title}
          className={`w-full object-cover transition-transform duration-300 group-hover:scale-105 ${
            large ? 'h-56' : 'h-44'
          }`}
        />
        {/* Platform badge */}
        <span
          className={`absolute top-3 left-3 ${platformColor} text-white text-xs font-semibold px-2.5 py-1 rounded-full shadow`}
        >
          {template.platform}
        </span>
        {/* Pricing badge */}
        <span
          className={`absolute top-3 right-3 text-xs font-bold px-2.5 py-1 rounded-full shadow ${
            template.price === null
              ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
              : 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
          }`}
        >
          {template.price === null ? 'FREE' : `$${template.price.toFixed(2)}`}
        </span>
      </div>

      {/* Content */}
      <div className="p-4">
        <h3
          className={`font-semibold text-gray-900 dark:text-white mb-1 ${
            large ? 'text-lg' : 'text-base'
          }`}
        >
          {template.title}
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2 mb-3">
          {template.description}
        </p>

        {/* Category tag */}
        <span className="inline-block text-xs font-medium bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 px-2 py-0.5 rounded-full mb-3">
          {getCategoryLabel(template.category)}
        </span>

        {/* Rating & downloads */}
        <div className="flex items-center gap-3 text-sm text-gray-500 dark:text-gray-400 mb-3">
          <span className="flex items-center gap-1">
            <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
            {template.rating}
          </span>
          <span className="flex items-center gap-1">
            <Download className="w-4 h-4" />
            {template.downloads.toLocaleString()}
          </span>
        </div>

        {/* Creator */}
        <p className="text-xs text-gray-400 dark:text-gray-500 mb-4">
          by{' '}
          <span className="font-medium text-gray-600 dark:text-gray-300">
            {template.creator}
          </span>
        </p>

        {/* Action buttons */}
        <div className="flex gap-2">
          <Link
            to={`/templates/${template.id}/preview`}
            className="flex-1 flex items-center justify-center gap-1.5 text-sm font-medium px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            <Eye className="w-4 h-4" />
            Preview
          </Link>
          <Link
            to={`/templates/${template.id}`}
            className="flex-1 flex items-center justify-center gap-1.5 text-sm font-medium px-3 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors"
          >
            Get Template
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </div>
  );
}

export default function TemplateBrowse() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedPlatform, setSelectedPlatform] = useState('All');
  const [selectedPricing, setSelectedPricing] = useState('All');

  const featuredTemplates = MOCK_TEMPLATES.filter((t) => t.featured);

  const filteredTemplates = useMemo(() => {
    return MOCK_TEMPLATES.filter((template) => {
      // Search filter
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const matchesSearch =
          template.title.toLowerCase().includes(q) ||
          template.description.toLowerCase().includes(q) ||
          template.creator.toLowerCase().includes(q);
        if (!matchesSearch) return false;
      }

      // Category filter
      if (selectedCategory && template.category !== selectedCategory) {
        return false;
      }

      // Platform filter
      if (
        selectedPlatform !== 'All' &&
        template.platform !== selectedPlatform
      ) {
        return false;
      }

      // Pricing filter
      if (selectedPricing === 'Free' && template.price !== null) return false;
      if (selectedPricing === 'Premium' && template.price === null) return false;

      return true;
    });
  }, [searchQuery, selectedCategory, selectedPlatform, selectedPricing]);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-700 text-white py-16 px-4">
        <div className="max-w-5xl mx-auto text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Sparkles className="w-8 h-8 text-yellow-300" />
            <h1 className="text-4xl md:text-5xl font-bold">
              Find the Perfect Template
            </h1>
          </div>
          <p className="text-lg md:text-xl text-blue-100 max-w-2xl mx-auto">
            Browse thousands of templates for Google Sheets, Excel, Notion,
            Airtable &amp; more
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search Bar */}
        <div className="relative max-w-2xl mx-auto mb-8">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search templates by name, description, or creator..."
            className="w-full pl-12 pr-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition"
          />
        </div>

        {/* Category Filter Bar */}
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-2">
            <Filter className="w-4 h-4 text-gray-500 dark:text-gray-400" />
            <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
              Categories
            </span>
          </div>
          <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
            <button
              onClick={() => setSelectedCategory(null)}
              className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                selectedCategory === null
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              All
            </button>
            {CATEGORIES.map((cat) => (
              <button
                key={cat.slug}
                onClick={() =>
                  setSelectedCategory(
                    selectedCategory === cat.slug ? null : cat.slug
                  )
                }
                className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  selectedCategory === cat.slug
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                {cat.emoji} {cat.label}
              </button>
            ))}
          </div>
        </div>

        {/* Platform & Pricing Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          {/* Platform Filter */}
          <div>
            <span className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1 block">
              Platform
            </span>
            <div className="flex gap-1 bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
              {PLATFORMS.map((platform) => (
                <button
                  key={platform}
                  onClick={() => setSelectedPlatform(platform)}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    selectedPlatform === platform
                      ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                  }`}
                >
                  {platform}
                </button>
              ))}
            </div>
          </div>

          {/* Pricing Filter */}
          <div>
            <span className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1 block">
              Pricing
            </span>
            <div className="flex gap-1 bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
              {PRICING_OPTIONS.map((option) => (
                <button
                  key={option}
                  onClick={() => setSelectedPricing(option)}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    selectedPricing === option
                      ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                  }`}
                >
                  {option}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Featured Section */}
        {selectedCategory === null &&
          selectedPlatform === 'All' &&
          selectedPricing === 'All' &&
          !searchQuery.trim() && (
            <section className="mb-12">
              <div className="flex items-center gap-2 mb-6">
                <Sparkles className="w-5 h-5 text-yellow-500" />
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                  Featured Templates
                </h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {featuredTemplates.map((template) => (
                  <TemplateCard
                    key={template.id}
                    template={template}
                    large
                  />
                ))}
              </div>
            </section>
          )}

        {/* Template Grid */}
        <section>
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              {selectedCategory
                ? getCategoryLabel(selectedCategory)
                : 'All Templates'}
            </h2>
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {filteredTemplates.length} template
              {filteredTemplates.length !== 1 ? 's' : ''} found
            </span>
          </div>

          {filteredTemplates.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredTemplates.map((template) => (
                <TemplateCard key={template.id} template={template} />
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <Search className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-2">
                No templates found
              </h3>
              <p className="text-gray-500 dark:text-gray-400">
                Try adjusting your filters or search query.
              </p>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
